/**
 * Converter Module
 *
 * Handles conversion between different openEHR formats using ehrtslib serialization modules.
 */

import {
  CANONICAL_JSON_CONFIG,
  CANONICAL_JSON_DESERIALIZE_CONFIG,
  COMPACT_JSON_CONFIG,
  COMPACT_JSON_DESERIALIZE_CONFIG,
  DEFAULT_JSON_DESERIALIZATION_CONFIG,
  DEFAULT_JSON_SERIALIZATION_CONFIG,
  HYBRID_JSON_CONFIG,
  HYBRID_JSON_DESERIALIZE_CONFIG,
  JsonCanonicalDeserializer,
  JsonCanonicalSerializer,
  JsonConfigurableDeserializer,
  JsonConfigurableSerializer,
  type JsonDeserializationConfig,
  type JsonSerializationConfig,
  NON_STANDARD_VERY_COMPACT_JSON_CONFIG,
  NON_STANDARD_VERY_COMPACT_JSON_DESERIALIZE_CONFIG,
} from "../../../serialization/json/mod.ts";

import {
  BLOCK_YAML_CONFIG,
  DEFAULT_YAML_DESERIALIZATION_CONFIG,
  DEFAULT_YAML_SERIALIZATION_CONFIG,
  FLOW_YAML_CONFIG,
  HYBRID_YAML_CONFIG,
  VERBOSE_YAML_CONFIG,
  type YamlDeserializationConfig,
  YamlDeserializer,
  type YamlSerializationConfig,
  YamlSerializer,
} from "../../../serialization/yaml/mod.ts";

import {
  XmlDeserializer,
  XmlSerializer,
} from "../../../serialization/xml/mod.ts";

import {
  CLINICAL_MARKDOWN_CONFIG,
  COMPACT_MARKDOWN_CONFIG,
  DEFAULT_MARKDOWN_SERIALIZATION_CONFIG,
  type MarkdownSerializationConfig,
  MarkdownSerializer,
  STRUCTURAL_MARKDOWN_CONFIG,
  WIKILINK_MARKDOWN_CONFIG,
} from "../../../serialization/markdown/mod.ts";

import {
  type AsciidocSerializationConfig,
  AsciidocSerializer,
  COMPACT_ASCIIDOC_CONFIG,
  DEFAULT_ASCIIDOC_SERIALIZATION_CONFIG,
  LOSSLESS_ASCIIDOC_CONFIG,
} from "../../../serialization/asciidoc/mod.ts";

import {
  type TypeScriptConstructorSerializationConfig,
  TypeScriptConstructorSerializer,
} from "../../../serialization/typescript/mod.ts";

import {
  buildWebTemplate,
  type CodedTextDisplayValue,
  type CodedTextMismatch,
  CodedTextMismatchError,
  type ContextDefaults,
  deserializeFromFlatWithIssues,
  deserializeFromStructuredWithIssues,
  type FlatRmDefaultIssue,
  isWebTemplateJson,
  type SimplifiedFlavor,
  type UnknownFlatKeys,
  UnknownFlatKeyError,
  parseWebTemplate,
  serializeToFlatJson,
  serializeToStructuredJson,
  type WebTemplate,
} from "../../../serialization/simplified/mod.ts";
import {
  type ClinicalModelWorkspace,
  getOperationalTemplateFromInput,
  parseTemplateInput,
  type TemplateWorkspace,
} from "../../../parser/mod.ts";
import {
  type GenerationMode,
  OptXmlSerializer,
  RMInstanceGenerator,
  TypeScriptGenerator,
} from "../../../generation/mod.ts";

import {
  DeserializationError,
  SerializationError,
  TypeRegistry,
} from "../../../serialization/common/mod.ts";

import {
  detectInputFormat,
  serializeToHtml5Variant,
  serializeToJZipehr,
  serializeToOptHtml5Variant,
  serializeToXZipehr,
  serializeToYZipehr,
  zipehrTextToCanonical,
} from "../../../serialization/zipehr/mod.ts";
import * as openehr_am from "../../../am/openehr_am.ts";
import { readTemplateId } from "../../../am/util/ontology_merge.ts";

// Import RM and Base modules for type registration
import * as openehr_rm from "../../../rm/openehr_rm.ts";
import * as openehr_base from "../../../base/openehr_base.ts";

// Global flag to track if TypeRegistry is initialized
let typeRegistryInitialized = false;

/**
 * Initialize the TypeRegistry with all RM types
 */
export function initializeTypeRegistry() {
  if (typeRegistryInitialized) {
    return;
  }

  try {
    // Register RM types
    TypeRegistry.registerModule(openehr_rm);

    // Register BASE types
    TypeRegistry.registerModule(openehr_base);

    typeRegistryInitialized = true;
    console.log(
      "✓ TypeRegistry initialized with",
      TypeRegistry.getAllTypeNames().length,
      "types",
    );
  } catch (error) {
    console.error("Failed to initialize TypeRegistry:", error);
    throw error;
  }
}

/**
 * Input format types
 */
export type InputFormat =
  | "auto"
  | "xml"
  | "json"
  | "yaml"
  | "zipehr"
  | "flat"
  | "structured";

/** Shown when FLAT/STRUCTURED (de)serialization needs a schema that is not loaded. */
export const MISSING_WEB_TEMPLATE_ERROR =
  "FLAT/STRUCTURED conversion requires a Web Template. Upload an operational template " +
  "(.opt, .oet, .adl, .adls, .zip, .t.json) or a Web Template JSON file using the " +
  "template upload control in the input panel (when using FLAT/STRUCTURED input) or " +
  "the Simplified output tab, or load a template on the Template input tab.";

/** Shown when OPT HTML5 output is requested without a loaded operational template. */
export const MISSING_OPT_FOR_HTML5_ERROR =
  "OPT HTML5 requires an operational template. Upload an OPT (.opt, .oet, .adl, .adls, " +
  ".zip, .t.json) using the template upload control on the OPT HTML5 tab or Simplified " +
  "tab, or load a template on the Template input tab.";

/** Shown when OPT XML output is requested without a loaded operational template. */
export const MISSING_OPT_FOR_XML_ERROR =
  "OPT XML requires an operational template. Upload an OPT (.opt, .oet, .adl, .adls, " +
  ".zip, .t.json) using the template upload control on the OPT XML tab or Simplified " +
  "tab, or load a template on the Template input tab.";

export type InputMode = "instance" | "template" | "template-adgit";
export type TemplateGenerationMode = GenerationMode;

/**
 * Output format types
 */
export type OutputFormat =
  | "xml"
  | "json"
  | "yaml"
  | "zipehr.json"
  | "zipehr.yaml"
  | "zipehr.xhtml"
  | "zipehr.html5.short"
  | "zipehr.html5.full"
  | "zipehr.html5.emoji"
  | "opt.html5.short"
  | "opt.html5.full"
  | "opt.html5.emoji"
  | "opt.xml"
  | "markdown"
  | "asciidoc"
  | "typescript"
  | "flat"
  | "structured"
  | "webtemplate";

/**
 * Conversion options
 */
export interface ConversionOptions {
  inputMode: InputMode;
  inputFormat: InputFormat;
  inputDeserializerConfig:
    | JsonDeserializationConfig
    | YamlDeserializationConfig;
  outputFormats: OutputFormat[];
  /** ZipEHR symbols for `zipehr.json` / `zipehr.yaml`, and for `zipehr.xhtml` title codes. */
  zipehrSymbolVariant?: "emoji" | "lettercode";
  /** ZipEHR HTML5 layout: oneliner | linesaving | fluffy. */
  zipehrHtml5Layout?: "oneliner" | "linesaving" | "fluffy";
  /** OPT HTML5 layout (same tristate as ZipEHR HTML5). */
  optHtml5Layout?: "oneliner" | "linesaving" | "fluffy";
  /**
   * How (X)HTML formats emit RM property names (`context`, `start_time`, …).
   * Default: omit when possible.
   */
  zipehrPropertyMode?: "omit" | "attribute" | "comment";
  /**
   * When emitting `opt.xml`, include path `<annotations>` (default true).
   * With `optXmlEmitL10n` also synthesize Better/AD `L10n.{lang}` keys from
   * the Web Template's localizedNames.
   */
  optXmlIncludeAnnotations?: boolean;
  /** Synthesize L10n.* annotations from Web Template names (default true). */
  optXmlEmitL10n?: boolean;
  templateGenerationMode: TemplateGenerationMode;
  templateLanguage?: string;
  jsonSerializerType: "canonical" | "configurable";
  jsonConfig: JsonSerializationConfig;
  yamlConfig: YamlSerializationConfig;
  markdownConfig: MarkdownSerializationConfig;
  asciidocConfig: AsciidocSerializationConfig;
  xmlConfig: {
    prettyPrint: boolean;
    indent: number;
    includeDeclaration: boolean;
    includeNamespaces: boolean;
  };
  typescriptConfig: TypeScriptConstructorSerializationConfig;
  /** FLAT/STRUCTURED input settings (ignored for other input formats). */
  simplifiedInput?: SimplifiedInputSettings;
  /** FLAT/STRUCTURED output flavour. Default `"output"` (no `ctx/`). */
  simplifiedFlavor?: SimplifiedFlavor;
  /** Loaded template/archetype file set (ADL2, OPT/OET, `.t.json`). */
  templateWorkspace?: TemplateWorkspace | ClinicalModelWorkspace;
}

/**
 * Pick which in-browser file set drives a conversion.
 *
 * Template / AD@git generation must use the clinical-model workspace even when
 * the instance tab has a published Web Template loaded for FLAT conversion.
 * Instance mode still prefers that simplified-schema workspace when present.
 */
export function workspaceForConversion(
  inputMode: InputMode,
  clinical: ClinicalModelWorkspace,
  simplified: ClinicalModelWorkspace,
): ClinicalModelWorkspace {
  if (inputMode === "template" || inputMode === "template-adgit") {
    return clinical;
  }
  if (simplified.listFiles().length > 0) {
    return simplified;
  }
  return clinical;
}

/**
 * Conversion result
 */
export interface ConversionResult {
  success: boolean;
  outputs?: {
    xml?: string;
    json?: string;
    yaml?: string;
    "zipehr.json"?: string;
    "zipehr.yaml"?: string;
    "zipehr.xhtml"?: string;
    "zipehr.html5.short"?: string;
    "zipehr.html5.full"?: string;
    "zipehr.html5.emoji"?: string;
    "opt.html5.short"?: string;
    "opt.html5.full"?: string;
    "opt.html5.emoji"?: string;
    "opt.xml"?: string;
    markdown?: string;
    asciidoc?: string;
    typescript?: string;
    flat?: string;
    structured?: string;
    webtemplate?: string;
  };
  error?: string;
  errorDetails?: any;
  /**
   * FLAT/STRUCTURED input issues that did not stop the conversion
   * (warnings and info, e.g. kept or corrected coded text, filled defaults).
   */
  inputIssues?: FlatRmDefaultIssue[];
}

/** FLAT/STRUCTURED deserializer settings shown in the demo. */
export interface SimplifiedInputSettings {
  codedTextMismatch?: CodedTextMismatch;
  codedTextDisplayValue?: CodedTextDisplayValue;
  contextDefaults?: ContextDefaults;
  unknownKeys?: UnknownFlatKeys;
}

/** JSON/YAML deserializer checkboxes shown in the demo. */
export interface JsonDeserializerChecks {
  strict: boolean;
  parseTerseFormat: boolean;
  allowIncomplete: boolean;
}

export type InputDeserializerPresetId =
  | "default"
  | "canonical"
  | "compact"
  | "hybrid"
  | "custom";

export interface InputDeserializerPreset {
  label: string;
  description: string;
  checks: JsonDeserializerChecks;
  simplified: Required<SimplifiedInputSettings>;
}

/**
 * Demo deserializer presets. One table drives the select, the checkboxes and
 * {@link resolveInputDeserializerSettings}. `custom` is not listed: it uses
 * the values the user set.
 */
export const INPUT_DESERIALIZER_PRESETS: Record<
  Exclude<InputDeserializerPresetId, "custom">,
  InputDeserializerPreset
> = {
  default: {
    label: "Default",
    description:
      "Standard openEHR input with type inference where _type is missing. FLAT/STRUCTURED: coded text that matches no template term is an error.",
    checks: { strict: false, parseTerseFormat: false, allowIncomplete: false },
    simplified: {
      codedTextMismatch: "reject",
      codedTextDisplayValue: "keep",
      contextDefaults: "apply",
      unknownKeys: "error",
    },
  },
  canonical: {
    label: "Canonical (Strict)",
    description:
      "Canonical openEHR JSON/YAML only: unknown properties and missing _type are errors. FLAT/STRUCTURED: coded-text mismatches and unknown keys are errors.",
    checks: { strict: true, parseTerseFormat: false, allowIncomplete: false },
    simplified: {
      codedTextMismatch: "reject",
      codedTextDisplayValue: "keep",
      contextDefaults: "apply",
      unknownKeys: "error",
    },
  },
  compact: {
    label: "Compact",
    description:
      "Compact JSON/YAML with terse strings such as \"ISO_639-1::en\". FLAT/STRUCTURED: coded text that matches a term except for case is corrected; unknown keys are warnings.",
    checks: { strict: false, parseTerseFormat: true, allowIncomplete: false },
    simplified: {
      codedTextMismatch: "correct",
      codedTextDisplayValue: "keep",
      contextDefaults: "apply",
      unknownKeys: "warning",
    },
  },
  hybrid: {
    label: "Hybrid (Flexible)",
    description:
      "Most lenient: terse strings and incomplete objects are accepted. FLAT/STRUCTURED: coded text is kept as sent, with a warning; unknown keys are warnings.",
    checks: { strict: false, parseTerseFormat: true, allowIncomplete: true },
    simplified: {
      codedTextMismatch: "keep",
      codedTextDisplayValue: "keep",
      contextDefaults: "apply",
      unknownKeys: "warning",
    },
  },
};

/**
 * Deserializer settings for a preset. `custom` uses `custom` as given; a
 * named preset ignores it.
 */
export function resolveInputDeserializerSettings(
  preset: string,
  custom?: {
    checks?: Partial<JsonDeserializerChecks>;
    simplified?: SimplifiedInputSettings;
  },
): {
  deserializerConfig: JsonDeserializationConfig;
  simplifiedInput: Required<SimplifiedInputSettings>;
} {
  const base = INPUT_DESERIALIZER_PRESETS[
    preset as keyof typeof INPUT_DESERIALIZER_PRESETS
  ] ?? INPUT_DESERIALIZER_PRESETS.default;
  const isCustom = preset === "custom";
  const checks = isCustom ? { ...base.checks, ...custom?.checks } : base.checks;
  const simplified = isCustom
    ? { ...base.simplified, ...custom?.simplified }
    : base.simplified;
  return {
    deserializerConfig: {
      ...DEFAULT_JSON_DESERIALIZATION_CONFIG,
      strict: checks.strict,
      parseTerseFormat: checks.parseTerseFormat,
      allowIncomplete: checks.allowIncomplete,
    },
    simplifiedInput: { ...simplified },
  };
}

/** Readable message for FLAT/STRUCTURED input errors that carry issues. */
export function describeConversionError(error: unknown): string {
  if (error instanceof CodedTextMismatchError) {
    return [
      `Coded text mismatch (${error.issues.length}):`,
      ...error.issues.map((issue) => `• ${issue.path}: ${issue.message}`),
      "Set “Coded text mismatch” to Keep or Correct in the deserializer settings to accept such input.",
    ].join("\n");
  }
  if (error instanceof UnknownFlatKeyError) {
    return [
      `Unknown FLAT keys (${error.issues.length}):`,
      ...error.issues.map((issue) => `• ${issue.path}: ${issue.message}`),
      "Set “Unknown keys” to Warning or Ignore in the deserializer settings to accept such input.",
    ].join("\n");
  }
  return (error as Error)?.message ?? String(error);
}

const SIMPLIFIED_OUTPUT_FORMATS = new Set<OutputFormat>([
  "flat",
  "structured",
  "webtemplate",
]);

const OPT_HTML5_OUTPUT_FORMATS = new Set<OutputFormat>([
  "opt.html5.short",
  "opt.html5.full",
  "opt.html5.emoji",
]);

function writeOptHtml5Outputs(
  opt: openehr_am.OPERATIONAL_TEMPLATE,
  outputFormats: OutputFormat[],
  outputs: Record<string, string>,
  layout?: "oneliner" | "linesaving" | "fluffy",
): void {
  for (const format of outputFormats) {
    if (!OPT_HTML5_OUTPUT_FORMATS.has(format)) continue;
    outputs[format] = serializeToOptHtml5Variant(
      opt,
      format as "opt.html5.short" | "opt.html5.full" | "opt.html5.emoji",
      { layout },
    );
  }
}

/** Resolve OPERATIONAL_TEMPLATE from workspace (required for OPT HTML5). */
export function resolveOperationalTemplateForOptHtml5(
  options: ConversionOptions,
): openehr_am.OPERATIONAL_TEMPLATE {
  const ws = options.templateWorkspace;
  if (!ws?.listFiles().length) {
    throw new Error(MISSING_OPT_FOR_HTML5_ERROR);
  }
  try {
    return ws.resolveOperational().operationalTemplate;
  } catch (error) {
    throw new Error(
      `${MISSING_OPT_FOR_HTML5_ERROR} (${
        error instanceof Error ? error.message : String(error)
      })`,
    );
  }
}

export function resolveOperationalTemplateForOptXml(
  options: ConversionOptions,
): openehr_am.OPERATIONAL_TEMPLATE {
  const ws = options.templateWorkspace;
  if (!ws?.listFiles().length) {
    throw new Error(MISSING_OPT_FOR_XML_ERROR);
  }
  try {
    return ws.resolveOperational().operationalTemplate;
  } catch (error) {
    throw new Error(
      `${MISSING_OPT_FOR_XML_ERROR} (${
        error instanceof Error ? error.message : String(error)
      })`,
    );
  }
}

function writeOptXmlOutput(
  opt: openehr_am.OPERATIONAL_TEMPLATE,
  outputFormats: OutputFormat[],
  outputs: Record<string, string>,
  options: ConversionOptions,
): void {
  if (!outputFormats.includes("opt.xml")) return;
  const includeAnnotations = options.optXmlIncludeAnnotations ?? true;
  const emitL10n = options.optXmlEmitL10n ?? true;
  const l10nFromWebTemplate = includeAnnotations && emitL10n
    ? buildWebTemplate(
      opt,
      options.templateLanguage
        ? { languages: [options.templateLanguage] }
        : undefined,
    )
    : undefined;
  outputs["opt.xml"] = new OptXmlSerializer({
    includeAnnotations,
    l10nFromWebTemplate,
  }).serialize(opt);
}

function writeSimplifiedOutputs(
  instance: unknown,
  webTemplate: WebTemplate,
  outputFormats: OutputFormat[],
  outputs: Record<string, string>,
  flavor: SimplifiedFlavor = "output",
): void {
  for (const format of outputFormats) {
    switch (format) {
      case "flat":
        outputs.flat = serializeToFlatJson(instance, webTemplate, {
          prettyPrint: true,
          flavor,
        });
        break;
      case "structured":
        outputs.structured = serializeToStructuredJson(instance, webTemplate, {
          prettyPrint: true,
          flavor,
        });
        break;
      case "webtemplate":
        outputs.webtemplate = JSON.stringify(webTemplate, null, 2);
        break;
    }
  }
}

/** Resolve a Web Template from the workspace (JSON file or OPT-derived). */
export function resolveWebTemplate(
  options: ConversionOptions,
): WebTemplate {
  const ws = options.templateWorkspace;
  if (!ws?.listFiles().length) {
    throw new Error(MISSING_WEB_TEMPLATE_ERROR);
  }

  for (const file of ws.listFiles()) {
    if (!file.path.toLowerCase().endsWith(".json")) continue;
    try {
      const parsed = JSON.parse(file.content);
      if (isWebTemplateJson(parsed)) {
        return parseWebTemplate(parsed);
      }
    } catch {
      // not a web template JSON file
    }
  }

  try {
    const { operationalTemplate } = ws.resolveOperational();
    return buildWebTemplate(
      operationalTemplate,
      options.templateLanguage
        ? { languages: [options.templateLanguage] }
        : undefined,
    );
  } catch (error) {
    throw new Error(
      `${MISSING_WEB_TEMPLATE_ERROR} (${
        error instanceof Error ? error.message : String(error)
      })`,
    );
  }
}

function resolveOperationalTemplate(
  input: string,
  options: ConversionOptions,
): unknown {
  if (options.templateWorkspace?.listFiles().length) {
    try {
      return options.templateWorkspace.resolveOperational().operationalTemplate;
    } catch (workspaceError) {
      if (!input.trim()) throw workspaceError;
      // Web Template JSON (instance FLAT schema) is not an operational
      // template — fall through and parse the editor / generation input.
    }
  }

  try {
    return getOperationalTemplateFromInput(input, {
      archetypeRepository: options.templateWorkspace?.repository,
    });
  } catch (firstError) {
    const parsed = parseTemplateInput(input, {
      archetypeRepository: options.templateWorkspace?.repository,
    });
    if (parsed.kind === "oet_document") {
      throw new Error(
        "OET template loaded but requires referenced archetypes to compile. " +
          `Base archetype: ${
            parsed.oet?.document.definitionArchetypeId ?? "unknown"
          }. ` +
          "Upload archetype .adl files or a ZIP containing the full file set. " +
          (firstError as Error).message,
      );
    }
    throw firstError;
  }
}

/**
 * Convert input data to requested output formats
 */
export async function convert(
  input: string,
  options: ConversionOptions,
): Promise<ConversionResult> {
  // Initialize TypeRegistry if not done yet
  if (!typeRegistryInitialized) {
    initializeTypeRegistry();
  }

  try {
    const inputMode = options.inputMode === "template-adgit"
      ? "template"
      : options.inputMode;

    if (inputMode === "template") {
      return await convertTemplateInput(input, options);
    }

    // Step 1: Deserialize input to RM object
    const { rmObject, issues: inputIssues } = await deserializeInput(
      input,
      options,
    );

    // Step 2: Serialize to requested output formats
    const outputs: Record<string, string> = {};
    const simplifiedFormats = options.outputFormats.filter((format) =>
      SIMPLIFIED_OUTPUT_FORMATS.has(format)
    );
    const optHtml5Formats = options.outputFormats.filter((format) =>
      OPT_HTML5_OUTPUT_FORMATS.has(format)
    );
    const wantsOptXml = options.outputFormats.includes("opt.xml");

    for (const format of options.outputFormats) {
      if (SIMPLIFIED_OUTPUT_FORMATS.has(format)) continue;
      if (OPT_HTML5_OUTPUT_FORMATS.has(format)) continue;
      if (format === "opt.xml") continue;
      try {
        switch (format) {
          case "xml":
            outputs.xml = serializeToXml(rmObject, options.xmlConfig);
            break;
          case "json":
            outputs.json = serializeToJson(
              rmObject,
              options.jsonSerializerType,
              options.jsonConfig,
            );
            break;
          case "yaml":
            outputs.yaml = serializeToYaml(rmObject, options.yamlConfig);
            break;
          case "zipehr.json":
            outputs["zipehr.json"] = await serializeToJZipehr(
              rmObject,
              options.zipehrSymbolVariant,
            );
            break;
          case "zipehr.yaml":
            outputs["zipehr.yaml"] = await serializeToYZipehr(
              rmObject,
              options.zipehrSymbolVariant,
            );
            break;
          case "zipehr.xhtml":
            outputs["zipehr.xhtml"] = await serializeToXZipehr(rmObject, {
              symbolVariant: options.zipehrSymbolVariant ?? "lettercode",
              propertyMode: options.zipehrPropertyMode,
            });
            break;
          case "zipehr.html5.short":
          case "zipehr.html5.full":
          case "zipehr.html5.emoji":
            outputs[format] = await serializeToHtml5Variant(
              rmObject,
              format,
              {
                layout: options.zipehrHtml5Layout,
                propertyMode: options.zipehrPropertyMode,
              },
            );
            break;
          case "markdown":
            outputs.markdown = serializeToMarkdown(
              rmObject,
              options.markdownConfig,
            );
            break;
          case "asciidoc":
            outputs.asciidoc = serializeToAsciidoc(
              rmObject,
              options.asciidocConfig,
            );
            break;
          case "typescript":
            outputs.typescript = generateTypeScriptCode(
              rmObject,
              options.typescriptConfig,
            );
            break;
        }
      } catch (error) {
        console.error(`Failed to serialize to ${format}:`, error);
        throw new Error(
          `Failed to convert to ${format}: ${(error as Error).message}`,
        );
      }
    }

    if (simplifiedFormats.length) {
      const webTemplate = resolveWebTemplate(options);
      writeSimplifiedOutputs(
        rmObject,
        webTemplate,
        simplifiedFormats,
        outputs,
        options.simplifiedFlavor,
      );
    }

    if (optHtml5Formats.length) {
      const opt = resolveOperationalTemplateForOptHtml5(options);
      writeOptHtml5Outputs(
        opt,
        optHtml5Formats,
        outputs,
        options.optHtml5Layout,
      );
    }

    if (wantsOptXml) {
      const opt = resolveOperationalTemplateForOptXml(options);
      writeOptXmlOutput(opt, options.outputFormats, outputs, options);
    }

    return {
      success: true,
      outputs,
      ...(inputIssues.length ? { inputIssues } : {}),
    };
  } catch (error) {
    console.error("Conversion error:", error);
    return {
      success: false,
      error: describeConversionError(error),
      errorDetails: error,
    };
  }
}

async function convertTemplateInput(
  input: string,
  options: ConversionOptions,
): Promise<ConversionResult> {
  const template = resolveOperationalTemplate(
    input,
    options,
  ) as openehr_am.OPERATIONAL_TEMPLATE;

  const generator = new RMInstanceGenerator({
    mode: options.templateGenerationMode,
    language: options.templateLanguage,
  });

  const generatedInstance = generator.generate(template);
  const outputs: Record<string, string> = {};

  for (const format of options.outputFormats) {
    if (SIMPLIFIED_OUTPUT_FORMATS.has(format)) continue;
    if (OPT_HTML5_OUTPUT_FORMATS.has(format)) continue;
    if (format === "opt.xml") continue;
    switch (format) {
      case "xml":
        outputs.xml = serializeToXml(generatedInstance, options.xmlConfig);
        break;
      case "json":
        outputs.json = serializeToJson(
          generatedInstance,
          options.jsonSerializerType,
          options.jsonConfig,
        );
        break;
      case "yaml":
        outputs.yaml = serializeToYaml(generatedInstance, options.yamlConfig);
        break;
      case "zipehr.json":
        outputs["zipehr.json"] = await serializeToJZipehr(
          generatedInstance,
          options.zipehrSymbolVariant,
        );
        break;
      case "zipehr.yaml":
        outputs["zipehr.yaml"] = await serializeToYZipehr(
          generatedInstance,
          options.zipehrSymbolVariant,
        );
        break;
      case "zipehr.xhtml":
        outputs["zipehr.xhtml"] = await serializeToXZipehr(generatedInstance, {
          symbolVariant: options.zipehrSymbolVariant ?? "lettercode",
          propertyMode: options.zipehrPropertyMode,
        });
        break;
      case "zipehr.html5.short":
      case "zipehr.html5.full":
      case "zipehr.html5.emoji":
        outputs[format] = await serializeToHtml5Variant(
          generatedInstance,
          format,
          {
            layout: options.zipehrHtml5Layout,
            propertyMode: options.zipehrPropertyMode,
          },
        );
        break;
      case "markdown":
        outputs.markdown = serializeToMarkdown(
          generatedInstance,
          options.markdownConfig,
        );
        break;
      case "asciidoc":
        outputs.asciidoc = serializeToAsciidoc(
          generatedInstance,
          options.asciidocConfig,
        );
        break;
      case "typescript": {
        const tsGenerator = new TypeScriptGenerator({
          language: options.templateLanguage || "en",
        });
        outputs.typescript = tsGenerator.generate(template);
        break;
      }
    }
  }

  writeSimplifiedOutputs(
    generatedInstance,
    buildWebTemplate(
      template,
      options.templateLanguage
        ? { languages: [options.templateLanguage] }
        : undefined,
    ),
    options.outputFormats,
    outputs,
    options.simplifiedFlavor,
  );

  writeOptHtml5Outputs(
    template,
    options.outputFormats,
    outputs,
    options.optHtml5Layout,
  );

  writeOptXmlOutput(template, options.outputFormats, outputs, options);

  return { success: true, outputs };
}

export function validateTemplateInput(
  input: string,
  workspace?: TemplateWorkspace | ClinicalModelWorkspace,
): { valid: boolean; message: string } {
  const text = input.trim();
  if (!text && !workspace?.listFiles().length) {
    return { valid: false, message: "Empty template" };
  }

  try {
    if (workspace?.listFiles().length) {
      const resolved = workspace.resolveOperational();
      const id = displayOperationalId(resolved.operationalTemplate);
      const kind = resolved.sourceKind;
      const fileCount = workspace.listFiles().length;
      const archCount = workspace.repository.listIds().length;
      const rootPath = workspace.getGenerationRootPath();
      const root = rootPath?.split("/").pop() ?? "?";
      const rootFile = rootPath ? workspace.getFile(rootPath) : undefined;
      const fmt = formatLabelForLoadKind(rootFile?.loadResult?.kind, kind);
      return {
        valid: true,
        message:
          `Valid ${fmt}: ${id} (root: ${root}, ${fileCount} files, ${archCount} archetypes)`,
      };
    }

    const parsed = parseTemplateInput(text, {
      archetypeRepository: workspace?.repository,
    });
    if (parsed.kind === "operational_template" && parsed.operationalTemplate) {
      const id = parsed.operationalTemplate.archetype_id?.value ??
        "operational template";
      const fmt = formatLabelForFormat(parsed.format);
      return { valid: true, message: `Valid ${fmt}: ${id}` };
    }
    if (parsed.kind === "oet_document" && parsed.oet) {
      const id = parsed.oet.document.definitionArchetypeId ??
        parsed.oet.document.name;
      return { valid: true, message: `Valid OET (needs archetypes): ${id}` };
    }
    if (parsed.kind === "template") {
      const fmt = formatLabelForFormat(parsed.format);
      return {
        valid: true,
        message: `Valid ${fmt} (flatten to operational for instances)`,
      };
    }
    return {
      valid: false,
      message: `Unsupported template kind: ${parsed.kind}`,
    };
  } catch (error) {
    return {
      valid: false,
      message: `Invalid template: ${(error as Error).message}`,
    };
  }
}

function displayOperationalId(opt: openehr_am.OPERATIONAL_TEMPLATE): string {
  const templateId = readTemplateId(opt);
  const archetypeId = opt.archetype_id?.value;
  if (templateId && archetypeId && templateId !== archetypeId) {
    return `${templateId} (${archetypeId})`;
  }
  return templateId ?? archetypeId ?? "operational template";
}

function formatLabelForLoadKind(
  loadKind: string | undefined,
  sourceKind: string,
): string {
  switch (loadKind) {
    case "template_json":
      return "Better template JSON";
    case "template":
      return "ADL2 template";
    case "operational_template":
      return "operational template";
    case "oet_xml":
      return "OET XML";
    case "opt_xml":
      return "OPT XML";
    default:
      switch (sourceKind) {
        case "adl2_template":
          return "ADL2 template";
        case "adl14_operational":
          return "ADL 1.4 operational template";
        case "oet_compiled":
          return "compiled OET";
        case "opt_xml":
          return "OPT XML";
        case "operational_template":
          return "operational template";
        default:
          return "template";
      }
  }
}

function formatLabelForFormat(format: string | undefined): string {
  switch (format) {
    case "template_json":
      return "Better template JSON";
    case "adl14":
      return "ADL 1.4 operational template";
    case "adl2":
      return "ADL2";
    case "oet_xml":
      return "OET XML";
    case "opt_xml":
      return "OPT XML";
    default:
      return "template";
  }
}

export function isSimplifiedInputFormat(
  format: string,
): format is "flat" | "structured" {
  return format === "flat" || format === "structured";
}

function detectSimplifiedJson(
  input: string,
): "flat" | "structured" | null {
  try {
    const obj = JSON.parse(input.trim()) as Record<string, unknown>;
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;
    const keys = Object.keys(obj);
    if (keys.some((k) => k.startsWith("ctx/"))) return "flat";
    if (
      "ctx" in obj && typeof obj.ctx === "object" && obj.ctx != null &&
      !Array.isArray(obj.ctx)
    ) {
      return "structured";
    }
    if ("_type" in obj) return null;
    if (keys.some((k) => k.includes("|"))) return "flat";
    // Output flavour: no ctx/, so look at the shape instead.
    if (
      keys.length > 0 &&
      keys.every((k) => k.includes("/")) &&
      Object.values(obj).every((v) => v === null || typeof v !== "object")
    ) {
      return "flat";
    }
    if (
      keys.length > 0 && keys.length <= 2 &&
      keys.every((k) => k === "ctx" || !k.includes("/")) &&
      hasPipeKey(obj, 8)
    ) {
      return "structured";
    }
  } catch {
    // not JSON
  }
  return null;
}

/** True when a STRUCTURED-style `|attribute` key occurs within `depth` levels. */
function hasPipeKey(value: unknown, depth: number): boolean {
  if (depth <= 0 || value === null || typeof value !== "object") return false;
  if (Array.isArray(value)) {
    return value.some((item) => hasPipeKey(item, depth - 1));
  }
  return Object.entries(value as Record<string, unknown>).some(([k, v]) =>
    k.startsWith("|") || hasPipeKey(v, depth - 1)
  );
}

type ResolvedWireFormat = "xml" | "json" | "yaml" | "flat" | "structured";

function zipehrWireFormat(
  variant: string,
): ResolvedWireFormat {
  if (variant === "zipehr.yaml") return "yaml";
  if (
    variant === "zipehr.xhtml" ||
    variant === "zipehr.html5.short" ||
    variant === "zipehr.html5.full" ||
    variant === "zipehr.html5.emoji"
  ) {
    return "xml";
  }
  return "json";
}

/**
 * Resolve effective input format (auto-detect zipehr, simplified, canonical).
 */
export function resolveInputFormat(
  input: string,
  requested: InputFormat,
): {
  format: ResolvedWireFormat;
  isZipehr: boolean;
  zipehrVariant?: string;
} {
  if (requested === "flat" || requested === "structured") {
    return { format: requested, isZipehr: false };
  }

  if (requested === "zipehr") {
    const detected = detectInputFormat(input);
    if (detected.kind === "zipehr") {
      return {
        format: zipehrWireFormat(detected.variant),
        isZipehr: true,
        zipehrVariant: detected.variant,
      };
    }
    throw new Error(
      "Input format set to ZipEHR but content does not look like zipehr.json, zipehr.yaml, zipehr.xhtml, or zipehr.html5.",
    );
  }

  if (requested !== "auto") {
    return { format: requested, isZipehr: false };
  }

  const detected = detectInputFormat(input);
  if (detected.kind === "zipehr") {
    return {
      format: zipehrWireFormat(detected.variant),
      isZipehr: true,
      zipehrVariant: detected.variant,
    };
  }
  if (detected.kind === "canonical") {
    return { format: detected.format, isZipehr: false };
  }

  const simplified = detectSimplifiedJson(input);
  if (simplified) {
    return { format: simplified, isZipehr: false };
  }

  // Fallback: try JSON then YAML
  try {
    JSON.parse(input.trim());
    return { format: "json", isZipehr: false };
  } catch {
    return { format: "yaml", isZipehr: false };
  }
}

/**
 * Deserialize input based on format
 */
async function deserializeInput(
  input: string,
  options: ConversionOptions,
): Promise<{ rmObject: any; issues: FlatRmDefaultIssue[] }> {
  const config = options.inputDeserializerConfig;
  const resolved = resolveInputFormat(input, options.inputFormat);
  const plain = (rmObject: any) => ({ rmObject, issues: [] });

  if (resolved.isZipehr) {
    const canonical = await zipehrTextToCanonical(input);
    const deserializer = new JsonConfigurableDeserializer(
      config as JsonDeserializationConfig,
    );
    return plain(deserializer.deserialize(JSON.stringify(canonical)));
  }

  switch (resolved.format) {
    case "flat":
    case "structured": {
      const webTemplate = resolveWebTemplate(options);
      const parsed = JSON.parse(input);
      const { composition, issues } = resolved.format === "flat"
        ? deserializeFromFlatWithIssues(
          parsed as Record<string, string | number | boolean | null>,
          webTemplate,
          options.simplifiedInput,
        )
        : deserializeFromStructuredWithIssues(
          parsed as Record<string, unknown>,
          webTemplate,
          options.simplifiedInput,
        );
      throwOnSimplifiedInputErrors(issues);
      return { rmObject: composition, issues };
    }
    case "json": {
      const deserializer = new JsonConfigurableDeserializer(
        config as JsonDeserializationConfig,
      );
      return plain(deserializer.deserialize(input));
    }
    case "yaml": {
      const deserializer = new YamlDeserializer(
        config as YamlDeserializationConfig,
      );
      return plain(deserializer.deserialize(input));
    }
    case "xml": {
      const deserializer = new XmlDeserializer();
      return plain(deserializer.deserialize(input));
    }
    default:
      throw new Error(`Unsupported input format: ${resolved.format}`);
  }
}

/** Same stop rules as `deserializeFromFlat`, but keeps the other issues. */
function throwOnSimplifiedInputErrors(issues: FlatRmDefaultIssue[]): void {
  const mismatches = issues.filter((issue) =>
    issue.kind === "coded-text-mismatch" && issue.severity === "error"
  );
  if (mismatches.length) throw new CodedTextMismatchError(mismatches);
  const unknown = issues.filter((issue) =>
    issue.kind === "unknown-key" && issue.severity === "error"
  );
  if (unknown.length) throw new UnknownFlatKeyError(unknown);
}

/**
 * Serialize RM object to JSON
 */
function serializeToJson(
  obj: any,
  serializerType: "canonical" | "configurable",
  config: JsonSerializationConfig,
): string {
  if (serializerType === "canonical") {
    const serializer = new JsonCanonicalSerializer();
    return serializer.serialize(obj, {
      prettyPrint: config.prettyPrint,
      indent: config.indent,
      archetypeNodeIdLocation: config.archetypeNodeIdLocation,
      nameLocation: config.nameLocation,
    });
  } else {
    const serializer = new JsonConfigurableSerializer(config);
    return serializer.serialize(obj);
  }
}

/**
 * Serialize RM object to YAML
 */
function serializeToYaml(obj: any, config: YamlSerializationConfig): string {
  const serializer = new YamlSerializer(config);
  return serializer.serialize(obj);
}

/**
 * Serialize RM object to Markdown
 */
function serializeToMarkdown(
  obj: any,
  config: MarkdownSerializationConfig,
): string {
  const serializer = new MarkdownSerializer(config);
  return serializer.serialize(obj);
}

/**
 * Serialize RM object to AsciiDoc
 */
function serializeToAsciidoc(
  obj: any,
  config: AsciidocSerializationConfig,
): string {
  const serializer = new AsciidocSerializer(config);
  return serializer.serialize(obj);
}

/**
 * Serialize RM object to XML
 */
function serializeToXml(
  obj: any,
  config: {
    prettyPrint: boolean;
    indent: number;
    includeDeclaration: boolean;
    includeNamespaces: boolean;
  },
): string {
  const serializer = new XmlSerializer({
    prettyPrint: config.prettyPrint,
    indent: " ".repeat(config.indent), // Convert number to string of spaces
    includeDeclaration: config.includeDeclaration,
    useNamespaces: config.includeNamespaces,
  });
  return serializer.serialize(obj);
}

/**
 * Generate TypeScript initialization code
 */
function generateTypeScriptCode(
  obj: any,
  config: TypeScriptConstructorSerializationConfig,
): string {
  const serializer = new TypeScriptConstructorSerializer(config);
  return serializer.serialize(obj);
}

/**
 * Get configuration presets
 */
export function getJsonConfigPreset(preset: string): JsonSerializationConfig {
  switch (preset) {
    case "canonical":
      return { ...CANONICAL_JSON_CONFIG };
    case "compact":
      return { ...COMPACT_JSON_CONFIG };
    case "hybrid":
      return { ...HYBRID_JSON_CONFIG };
    case "very-compact":
      return { ...NON_STANDARD_VERY_COMPACT_JSON_CONFIG };
    default:
      return { ...DEFAULT_JSON_SERIALIZATION_CONFIG };
  }
}

export function getJsonDeserializeConfigPreset(
  preset: string,
): JsonDeserializationConfig {
  switch (preset) {
    case "canonical":
      return { ...CANONICAL_JSON_DESERIALIZE_CONFIG };
    case "compact":
      return { ...COMPACT_JSON_DESERIALIZE_CONFIG };
    case "hybrid":
      return { ...HYBRID_JSON_DESERIALIZE_CONFIG };
    default:
      return { ...DEFAULT_JSON_DESERIALIZATION_CONFIG };
  }
}

export function getYamlConfigPreset(preset: string): YamlSerializationConfig {
  switch (preset) {
    case "verbose":
      return { ...VERBOSE_YAML_CONFIG };
    case "hybrid":
      return { ...HYBRID_YAML_CONFIG };
    case "flow":
      return { ...FLOW_YAML_CONFIG };
    case "block":
      return { ...BLOCK_YAML_CONFIG };
    default:
      return { ...DEFAULT_YAML_SERIALIZATION_CONFIG };
  }
}

export function getYamlDeserializeConfigPreset(
  preset: string,
): YamlDeserializationConfig {
  // For now, use default for all presets
  return { ...DEFAULT_YAML_DESERIALIZATION_CONFIG };
}

export function getMarkdownConfigPreset(
  preset: string,
): MarkdownSerializationConfig {
  switch (preset) {
    case "clinical":
      return { ...CLINICAL_MARKDOWN_CONFIG };
    case "compact":
      return { ...COMPACT_MARKDOWN_CONFIG };
    case "wikilink":
      return { ...WIKILINK_MARKDOWN_CONFIG };
    case "structural":
    default:
      return {
        ...DEFAULT_MARKDOWN_SERIALIZATION_CONFIG,
        ...STRUCTURAL_MARKDOWN_CONFIG,
      };
  }
}

export function getAsciidocConfigPreset(
  preset: string,
): AsciidocSerializationConfig {
  switch (preset) {
    case "compact":
      return { ...COMPACT_ASCIIDOC_CONFIG };
    case "lossless":
    default:
      return {
        ...DEFAULT_ASCIIDOC_SERIALIZATION_CONFIG,
        ...LOSSLESS_ASCIIDOC_CONFIG,
      };
  }
}
